"use client"

import { useState, useEffect } from "react"
import { X, User, ExternalLink, Check } from "lucide-react"
import axios from "axios"
import config from "../config"

const ApplicantsModal = ({ isOpen, onClose, request, showSuccessModal, showFailureModal }) => {
  const [applicants, setApplicants] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen && request) {
      fetchApplicants()
    }
  }, [isOpen, request])

  const fetchApplicants = async () => {
    if (!request) return

    setLoading(true)
    try {
      const token = localStorage.getItem("access_token")
      const providerIds =
        typeof request.service_providers === "string"
          ? request.service_providers.split(",").filter((p) => p.trim() !== "")
          : []

      const applicantsData = await Promise.all(
        providerIds.map(async (userId) => {
          const response = await axios.get(`${config.API_BASE_URL}/profiles/profile/user/${userId}/`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
          })
          return { ...response.data, user_id: userId }
        }),
      )

      // Filter to show only hired applicant if someone is assigned
      if (request.assigned_to_id) {
        const hiredApplicant = applicantsData.filter(
          (applicant) => String(applicant.user_id) === String(request.assigned_to_id),
        )
        setApplicants(hiredApplicant)
      } else {
        setApplicants(applicantsData)
      }
    } catch (error) {
      // console.error("Error fetching applicants:", error)
      showFailureModal("Failed to load applicants.")
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptBid = async (bidId, userId) => {
    try {
      const csrfToken = getCsrfTokenFromCookies()
      const response = await fetch(`${config.API_BASE_URL}/bids/${bidId}/accept/`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ user_id: userId }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      showSuccessModal("Service provider hired successfully!")

      // Update local state to show only the hired applicant
      setApplicants((prev) => prev.filter((applicant) => String(applicant.user_id) === String(userId)))
    } catch (error) {
      // console.error("Error accepting bid:", error)
      showFailureModal("Failed to hire service provider.")
    }
  }

  const getCsrfTokenFromCookies = () => {
    const cookies = document.cookie.split("; ")
    for (const cookie of cookies) {
      if (cookie.startsWith("csrftoken=")) {
        return cookie.split("=")[1]
      }
    }
    return null
  }

  const handleViewProfile = (slug) => {
    window.open(`/profile/${encodeURIComponent(slug)}`, "_blank")
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {request?.assigned_to_id ? "Service Provider" : "Applicants"}
              </h3>
              <p className="text-sm text-gray-600">{request?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors duration-150">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading applicants...</p>
            </div>
          ) : applicants.length > 0 ? (
            <div className="space-y-4">
              {applicants.map((applicant) => (
                <div
                  key={applicant.user_id}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-150"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-lg">
                          {applicant.first_name?.charAt(0)}
                          {applicant.last_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          {applicant.first_name} {applicant.last_name}
                        </h4>
                        <p className="text-gray-600">{applicant.profession || "Service Provider"}</p>
                      </div>
                    </div>
                    {request?.assigned_to_id && String(applicant.user_id) === String(request.assigned_to_id) && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        <Check size={14} />
                        Hired
                      </span>
                    )}
                  </div>

                  {applicant.bio && <p className="text-gray-700 mb-4 line-clamp-3">{applicant.bio}</p>}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleViewProfile(applicant.slug)}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-150"
                    >
                      <ExternalLink size={16} />
                      View Profile
                    </button>

                    {!request?.assigned_to_id && (
                      <button
                        onClick={() => handleAcceptBid(request.id, applicant.user_id)}
                        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors duration-150"
                      >
                        <Check size={16} />
                        Hire Provider
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <User size={48} className="mx-auto mb-4 text-gray-300" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Applicants Yet</h4>
              <p className="text-gray-600">No service providers have applied for this request yet.</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-150"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ApplicantsModal
